import { create } from 'zustand';

/**
 * Shared "is the app-shell top bar pinned?" flag.
 *
 * `HomeTopBar` owns the scroll detection (sentinel + hysteresis) and the
 * resulting compact/pinned state. Pages that stack their own `sticky`
 * element below the top bar (e.g. the wallet's Deposit/Withdraw tabs) read
 * this so they can mirror the header's pinned styling without duplicating the
 * scroll listener.
 */
interface StickyHeaderState {
  /** True while the top bar is pinned to the viewport (scrolled past its scroll-away block). */
  isStuck: boolean;
  /** Publish the pinned state. No-ops when unchanged so subscribers don't re-render needlessly. */
  setStuck: (stuck: boolean) => void;
}

const useStickyHeaderStore = create<StickyHeaderState>((set) => ({
  isStuck: false,
  setStuck: (stuck) => set((s) => (s.isStuck === stuck ? s : { isStuck: stuck })),
}));

/** Whether the app-shell top bar is currently pinned to the viewport. */
export const useHeaderStuck = (): boolean => useStickyHeaderStore((s) => s.isStuck);

/**
 * Imperative setter for `HomeTopBar` to publish its pinned state from inside a
 * scroll handler. A plain function (not a hook) so it can be called outside the
 * React render path.
 */
export const setHeaderStuck = (stuck: boolean): void => useStickyHeaderStore.getState().setStuck(stuck);
